/**
 * ReviewProgressBar - Progress indicator for image review workflow.
 *
 * Shows "X of Y reviewed" with animated progress bar.
 * Displays completion percentage and celebrates 100%.
 *
 * @see /src/features/gallery/pages/GalleryReviewPage.tsx - Parent component
 */

import { cn } from '@/lib/utils'
import { motion } from 'framer-motion'
import { CheckCircle, PartyPopper } from 'lucide-react'

interface ReviewProgressBarProps {
  /** Number of images reviewed (approved + rejected) */
  reviewed: number
  /** Total number of images to review */
  total: number
  /** Additional className */
  className?: string
  /** Show detailed breakdown */
  showDetails?: boolean
  /** Breakdown counts */
  breakdown?: {
    approved: number
    rejected: number
    pending: number
  }
}

export function ReviewProgressBar({
  reviewed,
  total,
  className,
  showDetails = false,
  breakdown,
}: ReviewProgressBarProps) {
  const percentage = total > 0 ? Math.round((reviewed / total) * 100) : 0
  const isComplete = percentage === 100 && total > 0

  return (
    <div className={cn('space-y-2', className)}>
      {/* Header row */}
      <div className="flex items-center justify-between text-sm">
        <div className="flex items-center gap-2">
          {isComplete ? (
            <>
              <PartyPopper className="h-4 w-4 text-success" />
              <span className="font-medium text-success">All reviewed!</span>
            </>
          ) : (
            <>
              <CheckCircle className="h-4 w-4 text-muted-foreground" />
              <span className="text-muted-foreground">
                <span className="font-medium text-foreground">{reviewed}</span>
                {' of '}
                <span className="font-medium text-foreground">{total}</span>
                {' reviewed'}
              </span>
            </>
          )}
        </div>
        <span
          className={cn(
            'tabular-nums font-medium',
            isComplete ? 'text-success' : 'text-muted-foreground'
          )}
        >
          {percentage}%
        </span>
      </div>

      {/* Progress bar */}
      <div className="h-2 bg-muted rounded-full overflow-hidden">
        <motion.div
          className={cn(
            'h-full rounded-full',
            isComplete ? 'bg-success' : 'bg-primary'
          )}
          initial={{ width: 0 }}
          animate={{ width: `${percentage}%` }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
        />
      </div>

      {/* Detailed breakdown */}
      {showDetails && breakdown && (
        <div className="flex items-center gap-4 text-xs text-muted-foreground">
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-success" />
            {breakdown.approved} approved
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-destructive" />
            {breakdown.rejected} rejected
          </span>
          <span className="flex items-center gap-1">
            <span className="w-2 h-2 rounded-full bg-warning" />
            {breakdown.pending} pending
          </span>
        </div>
      )}
    </div>
  )
}

/**
 * Compact progress indicator for tight spaces.
 * Shows just the percentage with a small ring.
 */
export function ReviewProgressRing({
  reviewed,
  total,
  size = 40,
  strokeWidth = 4,
  className,
}: Omit<ReviewProgressBarProps, 'showDetails' | 'breakdown'> & {
  size?: number
  strokeWidth?: number
}) {
  const percentage = total > 0 ? Math.round((reviewed / total) * 100) : 0
  const isComplete = percentage === 100 && total > 0

  const radius = (size - strokeWidth) / 2
  const circumference = 2 * Math.PI * radius
  const strokeDashoffset = circumference - (percentage / 100) * circumference

  return (
    <div
      className={cn('relative inline-flex items-center justify-center', className)}
      style={{ width: size, height: size }}
    >
      <svg
        width={size}
        height={size}
        className="transform -rotate-90"
      >
        {/* Background circle */}
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          className="text-muted"
        />
        {/* Progress circle */}
        <motion.circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="currentColor"
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          className={isComplete ? 'text-success' : 'text-primary'}
          initial={{ strokeDashoffset: circumference }}
          animate={{ strokeDashoffset }}
          transition={{ duration: 0.5, ease: 'easeOut' }}
          style={{
            strokeDasharray: circumference,
          }}
        />
      </svg>
      <span
        className={cn(
          'absolute text-xs font-semibold tabular-nums',
          isComplete ? 'text-success' : 'text-foreground'
        )}
      >
        {percentage}%
      </span>
    </div>
  )
}
